import { Fragment } from "react";

import type { SuggestionDetail } from "@/lib/types";

type PhaseState = "done" | "active" | "pending" | "failed";
type Phase = { key: string; state: PhaseState; label: string; text: string };

// Short labels for the stepper markers; the longer `describe()` line is the status underneath.
const LABELS: Record<string, string> = {
  submitted: "Submitted",
  process: "Review",
  feedback: "Changes",
  critic: "Quality",
  security: "Security",
  approve: "Approval",
};

// A human-readable line per phase, varying by state.
function describe(key: string, state: PhaseState, s: SuggestionDetail): string {
  switch (key) {
    case "submitted":
      return "Suggestion received";
    case "process":
      return state === "done" ? "Review started" : "Start process";
    case "feedback":
      return state === "done" ? `Changes prepared${s.version_bump ? ` (${s.version_bump})` : ""}`
        : state === "active" ? "Preparing changes…"
        : state === "failed" ? "Could not prepare changes"
        : "Changes to be prepared";
    case "critic":
      return state === "done" ? "Quality check passed"
        : state === "active" ? "Quality check in progress…"
        : state === "failed" ? "Quality check failed"
        : "Quality check pending";
    case "security":
      return state === "done" ? "Security check passed"
        : state === "active" ? "Security check in progress…"
        : state === "failed" ? "Security concerns found"
        : "Security check pending";
    case "approve":
      return state === "done" ? "Owner approved & merged"
        : state === "active" ? "Awaiting owner approval"
        : state === "failed" ? "Owner rejected"
        : "Awaiting owner approval";
    default:
      return key;
  }
}

// Full lifecycle with both owner touchpoints (Process to start, Approval to merge).
// The pipeline is sequential, so progress is MONOTONIC: reaching a later stage means every
// earlier one passed — even if that stage's individual report wasn't persisted (e.g. a suggestion
// merged without a stored critic/security report must still show those phases green).
function phasesFor(s: SuggestionDetail): Phase[] {
  const keys = ["process", "feedback", "critic", "security", "approve"];

  const started = s.status !== "pending"; // owner has clicked Process
  const rejected = s.status === "rejected";

  // Highest phase index the suggestion has completed. Take the max over every signal so a missing
  // intermediate report never leaves an earlier phase looking "not reached".
  let maxDone = -1;
  const bump = (i: number) => { if (i > maxDone) maxDone = i; };
  if (started) bump(0);                     // process
  if (s.feedback_report) bump(1);           // changes
  if (s.critic_report) bump(2);             // quality
  if (s.security_report) bump(3);           // security
  if (s.status === "ready") bump(3);        // passed review, awaiting approval
  if (s.status === "merged") bump(4);       // everything passed and merged

  // Which phase failed (rejected only). owner → approval; a named stage → that phase; anything
  // else → the first phase not yet completed. Reaching a rejection stage implies the ones before
  // it passed, so pull maxDone up to just before the failed phase.
  let rejKey: string | undefined;
  if (rejected) {
    if (s.reject_stage === "owner") rejKey = "approve";
    else if (s.reject_stage && keys.includes(s.reject_stage)) rejKey = s.reject_stage;
    else rejKey = keys[Math.min(maxDone + 1, keys.length - 1)];
    bump(keys.indexOf(rejKey) - 1);
  }

  const inFlight = !rejected && s.status !== "merged"; // pending / processing / ready

  let assignedActive = false;
  return keys.map((key, i) => {
    let state: PhaseState;
    if (rejected && key === rejKey) state = "failed";
    else if (i <= maxDone) state = "done";
    else if (inFlight && !assignedActive) {
      state = "active";
      assignedActive = true;
    } else state = "pending";
    return { key, state, label: LABELS[key] ?? key, text: describe(key, state, s) };
  });
}

// -- Presentation ------------------------------------------------------------

const ic = "h-4 w-4";
function CheckIcon() { return (<svg className={ic} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg>); }
function XIcon() { return (<svg className={ic} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6L6 18M6 6l12 12" /></svg>); }

function Icon({ state }: { state: PhaseState }) {
  const base = "flex h-8 w-8 shrink-0 items-center justify-center rounded-full";
  if (state === "done")
    return <span className={`${base} bg-emerald-500/15 text-emerald-600 dark:text-emerald-400`}><CheckIcon /></span>;
  if (state === "failed")
    return <span className={`${base} bg-red-500/15 text-red-600 dark:text-red-400`}><XIcon /></span>;
  if (state === "active")
    return (
      <span className={`${base} bg-primary/15`}>
        <span className="h-4 w-4 rounded-full border-2 border-accent/30 border-t-accent motion-safe:animate-spin" />
      </span>
    );
  return (
    <span className={`${base} border border-black/12 bg-black/[0.02] dark:border-white/15 dark:bg-white/[0.03]`}>
      <span className="h-1.5 w-1.5 rounded-full bg-text/25" />
    </span>
  );
}

function labelClass(state: PhaseState): string {
  return state === "pending" ? "text-text/50" : "text-text";
}
function noteClass(state: PhaseState): string {
  return state === "failed" ? "text-red-600 dark:text-red-300/90"
    : state === "done" ? "text-emerald-700 dark:text-emerald-300/80"
    : state === "active" ? "text-text/75"
    : "text-text/45";
}
// Connector between two step icons — emerald once the earlier step is done, neutral otherwise.
function connectorClass(filled: boolean): string {
  return filled ? "bg-emerald-500/70" : "bg-black/20 dark:bg-white/25";
}

export default function SuggestionProgress({
  suggestion,
  orientation = "auto",
}: {
  suggestion: SuggestionDetail;
  // "auto" = horizontal stepper on very wide viewports, vertical timeline otherwise.
  // "vertical" = always the vertical timeline (used inside the narrow sidebar column).
  orientation?: "auto" | "vertical";
}) {
  const phases = phasesFor(suggestion);
  const last = phases.length - 1;
  const responsive = orientation === "auto";

  return (
    <div className="rounded-2xl border border-black/8 bg-black/[0.015] p-5 dark:border-white/10 dark:bg-white/[0.02]">
      {/* Wide screens (md+): a horizontal connected stepper that fills each column evenly.
          Below md it would read as a cramped, merged strip, so it drops to the vertical timeline
          below that (and never renders when forced vertical). */}
      {responsive && (
        <ol className="hidden items-start md:flex">
          {phases.map((p, i) => (
            <li key={p.key} className="flex flex-1 flex-col items-center text-center">
              {/* icon row with the connector halves reaching to the neighbouring columns */}
              <div className="flex w-full items-center">
                <span
                  aria-hidden
                  className={`h-[3px] flex-1 rounded-full ${i === 0 ? "opacity-0" : connectorClass(phases[i - 1].state === "done")}`}
                />
                <Icon state={p.state} />
                <span
                  aria-hidden
                  className={`h-[3px] flex-1 rounded-full ${i === last ? "opacity-0" : connectorClass(p.state === "done")}`}
                />
              </div>
              <p className={`mt-2.5 text-xs font-medium ${labelClass(p.state)}`}>{p.label}</p>
              <p className={`mt-0.5 max-w-[9.5rem] text-[11px] leading-snug ${noteClass(p.state)}`}>{p.text}</p>
            </li>
          ))}
        </ol>
      )}

      {/* Vertical timeline — the rail runs through the icon centres. */}
      <ol className={responsive ? "md:hidden" : ""}>
        {phases.map((p, i) => (
          <Fragment key={p.key}>
            <li className="relative flex gap-3">
              {i < last && (
                <span
                  aria-hidden
                  className={`absolute left-[14px] top-8 h-[calc(100%-1rem)] w-[3px] rounded-full ${connectorClass(p.state === "done")}`}
                />
              )}
              <Icon state={p.state} />
              <div className="min-w-0 flex-1 pb-5">
                <p className={`text-sm font-medium ${labelClass(p.state)}`}>{p.label}</p>
                <p className={`mt-0.5 text-xs leading-snug ${noteClass(p.state)}`}>{p.text}</p>
              </div>
            </li>
          </Fragment>
        ))}
      </ol>
    </div>
  );
}
